/**
 * Componentes de tela comuns: cabeçalho, KPIs, filtros, lista + detalhe, tabela densa, abas, formulário, diálogo de
 * motivo, estados e avisos — Nexora Grid (frontend, US-4601, Sprint 46).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Padrões do guia de layout (documentacao/19_guia_layout_e_experiencia.md §4) e do protótipo aprovado da tela de
 * cadastro. Estilos em `tela.css` (só tokens). Regras puras (filtros no endereço, ordenação, teclado) em
 * `regrasTela.ts`, testadas em `testes/tela.test.mjs`.
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { indiceVizinho, type Ordem } from "./regrasTela";
import "./tela.css";

// ---------------------------------------------------------------------------------------------------------------------
// Cabeçalho e migalhas
// ---------------------------------------------------------------------------------------------------------------------

export type Migalha = { texto: string; para?: string };

const VOLTA = "nexora.volta:";
/** A lista guarda o próprio endereço (filtros e seleção) para o "Voltar" da ficha devolver a pessoa ao mesmo ponto. */
export function useLembrarEndereco(): void {
  const local = useLocation();
  useEffect(() => {
    try { sessionStorage.setItem(VOLTA + local.pathname, local.pathname + local.search); } catch { /* sem armazenamento */ }
  }, [local.pathname, local.search]);
}

/** Endereço de volta: a lista como a pessoa deixou (filtros, seleção) ou a própria base. */
export function enderecoDeVolta(base: string): string {
  try { return sessionStorage.getItem(VOLTA + base) ?? base; } catch { return base; }
}

export function CabecalhoTela({ migalhas, titulo, subtitulo, selos, acoes, voltar }: {
  migalhas?: Migalha[]; titulo: ReactNode; subtitulo?: ReactNode; selos?: ReactNode; acoes?: ReactNode; voltar?: string;
}) {
  return (
    <>
      {migalhas && migalhas.length > 0 && (
        <nav className="tl-migalhas" aria-label="Você está em">
          {migalhas.map((m, i) => (
            <span key={i}>{i > 0 && <span aria-hidden> › </span>}
              {m.para && i < migalhas.length - 1 ? <Link to={enderecoDeVolta(m.para)}>{m.texto}</Link> : <span aria-current={i === migalhas.length - 1 ? "page" : undefined}>{m.texto}</span>}
            </span>
          ))}
        </nav>
      )}
      <div className="tl-cabecalho">
        {voltar && <Link to={enderecoDeVolta(voltar)} className="tl-fantasma" aria-label="Voltar">‹ Voltar</Link>}
        <h1>{titulo}</h1>
        {subtitulo && <span className="tl-subtitulo">{subtitulo}</span>}
        {selos}
        {acoes && <div className="tl-acoes">{acoes}</div>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// KPIs que filtram
// ---------------------------------------------------------------------------------------------------------------------

export type Kpi = { chave: string; rotulo: string; valor: ReactNode; detalhe?: ReactNode; tom?: "alarme" | "critico" | "semcom" };

export function FaixaKpis({ itens, ativo, aoEscolher }: { itens: Kpi[]; ativo?: string; aoEscolher?: (chave: string) => void }) {
  return (
    <div className="tl-kpis" style={{ ["--tl-kpis" as string]: String(Math.min(itens.length, 6)) }}>
      {itens.map((k) => aoEscolher ? (
        <button key={k.chave} type="button" className={`tl-kpi ${k.tom ?? ""}`} aria-pressed={ativo === k.chave}
          onClick={() => aoEscolher(ativo === k.chave ? "" : k.chave)}>
          <small>{k.rotulo}</small><strong>{k.valor}</strong>{k.detalhe && <span className="tl-det">{k.detalhe}</span>}
        </button>
      ) : (
        <div key={k.chave} className={`tl-kpi ${k.tom ?? ""}`}>
          <small>{k.rotulo}</small><strong>{k.valor}</strong>{k.detalhe && <span className="tl-det">{k.detalhe}</span>}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------------------------------------------------

export type Pilula = { chave: string; texto: string };

export function BarraFiltros({ busca, aoBuscar, placeholder, children, pilulas = [], aoTirar, aoLimpar }: {
  busca: string; aoBuscar: (texto: string) => void; placeholder: string; children?: ReactNode;
  pilulas?: Pilula[]; aoTirar?: (chave: string) => void; aoLimpar?: () => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const id = useId();
  // o texto fica local enquanto a pessoa digita e vai para o endereço logo depois (cada tecla no endereço perderia letras)
  const [texto, setTexto] = useState(busca);
  const espera = useRef<number | undefined>(undefined);
  useEffect(() => { if (busca !== texto && document.activeElement !== campo.current) setTexto(busca); }, [busca]);  // eslint-disable-line react-hooks/exhaustive-deps
  const digitar = (v: string) => {
    setTexto(v);
    window.clearTimeout(espera.current);
    espera.current = window.setTimeout(() => aoBuscar(v), 120);
  };
  useEffect(() => {
    const tecla = (ev: globalThis.KeyboardEvent) => {
      const alvo = document.activeElement?.tagName ?? "";
      if (ev.key === "/" && !/INPUT|TEXTAREA|SELECT/.test(alvo)) { ev.preventDefault(); campo.current?.focus(); }
    };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, []);
  return (
    <>
      <div className="tl-filtros" role="search">
        <div className="tl-busca">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
          <label htmlFor={id} className="visualmente-oculto">Buscar</label>
          <input ref={campo} id={id} type="search" placeholder={placeholder} value={texto} autoComplete="off"
            onChange={(e) => digitar(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") { setTexto(""); aoBuscar(""); campo.current?.blur(); } }} />
          <kbd aria-hidden>/</kbd>
        </div>
        {children}
      </div>
      <div className="tl-pilulas" aria-live="polite">{pilulas.length > 0 && (
        <>
          <span className="nx-suave">Filtros:</span>
          {pilulas.map((p) => (
            <span key={p.chave} className="tl-pilula">{p.texto}
              <button type="button" aria-label={`Tirar o filtro ${p.texto}`} onClick={() => aoTirar?.(p.chave)}>×</button></span>
          ))}
          {aoLimpar && <button type="button" className="tl-fantasma" style={{ minHeight: 22, padding: "0 6px" }} onClick={aoLimpar}>Limpar tudo</button>}
        </>
      )}</div>
    </>
  );
}

export function Segmentado<T extends string>({ opcoes, valor, aoMudar, rotulo }: {
  opcoes: { valor: T; texto: string }[]; valor: T; aoMudar: (v: T) => void; rotulo: string;
}) {
  return (
    <div className="tl-segmentado" role="group" aria-label={rotulo}>
      {opcoes.map((o) => <button key={o.valor} type="button" aria-pressed={valor === o.valor} onClick={() => aoMudar(o.valor)}>{o.texto}</button>)}
    </div>
  );
}

/** Estado guardado no endereço (`?chave=valor`): o link pode ser compartilhado e o "Voltar" do navegador funciona. */
export function useParametro(chave: string, padrao = ""): [string, (valor: string) => void] {
  const [parametros, setParametros] = useSearchParams();
  const valor = parametros.get(chave) ?? padrao;
  const definir = useCallback((novo: string) => {
    // parte do endereço atual (não do da última renderização): vários filtros trocados juntos ("Limpar tudo") somam
    setParametros(() => {
      const p = new URLSearchParams(window.location.search);
      if (novo && novo !== padrao) p.set(chave, novo); else p.delete(chave);
      return p;
    }, { replace: true });
  }, [chave, padrao, setParametros]);
  return [valor, definir];
}

// ---------------------------------------------------------------------------------------------------------------------
// Lista + detalhe
// ---------------------------------------------------------------------------------------------------------------------

export function ListaDetalhe({ lista, detalhe, aberto, aoFechar, larguraDetalhe }: {
  lista: ReactNode; detalhe?: ReactNode; aberto?: boolean; aoFechar?: () => void; larguraDetalhe?: number;
}) {
  return (
    <div className={`tl-lista-detalhe${detalhe ? "" : " sem-detalhe"}`}
      style={larguraDetalhe ? { ["--tl-largura-detalhe" as string]: `${larguraDetalhe}px` } : undefined}>
      {lista}
      {detalhe && (
        <aside className={`tl-bloco tl-detalhe${aberto ? " aberto" : ""}`} aria-label="Detalhe">
          {aoFechar && <button type="button" className="tl-fantasma tl-fechar" style={{ alignSelf: "flex-end" }} onClick={aoFechar}
            aria-label="Fechar o detalhe">×</button>}
          {detalhe}
        </aside>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Tabela densa (vira cartões no celular)
// ---------------------------------------------------------------------------------------------------------------------

export type Coluna<T> = { chave: string; titulo: string; celula: (linha: T) => ReactNode; ordenavel?: boolean; quebra?: boolean; oculto?: boolean };

export function TabelaDensa<T>({ colunas, linhas, chave, selecionada, aoSelecionar, aoAbrir, ordem, aoOrdenar, vazio, cartao, cabecalho, rotulo }: {
  colunas: Coluna<T>[]; linhas: T[]; chave: (l: T) => string; selecionada?: string | null;
  aoSelecionar?: (l: T) => void; aoAbrir?: (l: T) => void; ordem?: Ordem; aoOrdenar?: (o: Ordem) => void;
  vazio?: ReactNode; cartao?: (l: T) => ReactNode; cabecalho?: ReactNode; rotulo: string;
}) {
  const corpo = useRef<HTMLDivElement>(null);
  const teclado = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (!linhas.length || /INPUT|TEXTAREA|SELECT/.test((ev.target as HTMLElement).tagName)) return;
    const atual = linhas.findIndex((l) => chave(l) === selecionada);
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      const proxima = linhas[indiceVizinho(atual, linhas.length, ev.key === "ArrowDown" ? 1 : -1)];
      aoSelecionar?.(proxima);
      corpo.current?.querySelector(`[data-chave="${CSS.escape(chave(proxima))}"]`)?.scrollIntoView({ block: "nearest" });
    } else if (ev.key === "Enter" && atual >= 0 && aoAbrir && (ev.target as HTMLElement).tagName !== "BUTTON") {
      aoAbrir(linhas[atual]);
    }
  };
  const clique = (l: T) => {
    if (aoAbrir && selecionada === chave(l) && window.innerWidth > 1024) aoAbrir(l); else aoSelecionar?.(l);
  };
  const visiveis = colunas.filter((c) => !c.oculto);
  return (
    <section className="tl-bloco tl-tabela-ou-cartoes" aria-label={rotulo}>
      {cabecalho && <div className="tl-lista-cab">{cabecalho}</div>}
      <div className="tl-rolagem" ref={corpo} tabIndex={0} onKeyDown={teclado} aria-label={`${rotulo}: use ↑ ↓ para escolher e Enter para abrir`}>
        {linhas.length === 0 ? (vazio ?? <EstadoVazio titulo="Nada para mostrar" />) : (
          <>
            <table className="tl-tabela">
              <thead><tr>{visiveis.map((c) => (
                <th key={c.chave} scope="col" aria-sort={ordem?.coluna === c.chave ? (ordem.crescente ? "ascending" : "descending") : undefined}>
                  {c.ordenavel && aoOrdenar ? (
                    <button type="button" onClick={() => aoOrdenar({ coluna: c.chave, crescente: ordem?.coluna === c.chave ? !ordem.crescente : true })}>
                      {c.titulo}{ordem?.coluna === c.chave ? (ordem.crescente ? " ↑" : " ↓") : ""}</button>
                  ) : c.titulo}
                </th>
              ))}</tr></thead>
              <tbody>{linhas.map((l) => (
                <tr key={chave(l)} data-chave={chave(l)} aria-selected={selecionada === chave(l)} onClick={() => clique(l)}>
                  {visiveis.map((c) => <td key={c.chave} className={c.quebra ? "tl-quebra" : undefined}>{c.celula(l)}</td>)}
                </tr>
              ))}</tbody>
            </table>
            {cartao && (
              <div className="tl-cartoes">{linhas.map((l) => (
                <button key={chave(l)} type="button" className="tl-cartao" aria-selected={selecionada === chave(l)} onClick={() => aoSelecionar?.(l)}>
                  {cartao(l)}</button>
              ))}</div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Selos e estado (alarme sempre com forma — ISA-101)
// ---------------------------------------------------------------------------------------------------------------------

export type Estado = "critico" | "alto" | "medio" | "baixo" | "semcom" | "normal" | "semtel";
const FORMAS: Record<Estado, string> = { critico: "◆", alto: "▲", medio: "●", baixo: "■", semcom: "◌", normal: "", semtel: "" };
const TEXTOS_ESTADO: Record<Estado, string> = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo", semcom: "Sem comunicação",
  normal: "Normal", semtel: "Sem telemetria" };

export function EstadoAlarme({ estado, texto }: { estado: Estado; texto?: string }) {
  return <span className={`tl-estado ${estado}`}>{FORMAS[estado] && <span className="tl-forma" aria-hidden>{FORMAS[estado]}</span>}{texto ?? TEXTOS_ESTADO[estado]}</span>;
}

export function Selo({ tom, children }: { tom?: "ok" | "atencao" | "suave" | "ruim"; children: ReactNode }) {
  return <span className={`tl-selo ${tom ?? ""}`}>{children}</span>;
}

// ---------------------------------------------------------------------------------------------------------------------
// Abas
// ---------------------------------------------------------------------------------------------------------------------

export function Abas<T extends string>({ abas, ativa, aoMudar, rotulo }: { abas: { chave: T; texto: string }[]; ativa: T; aoMudar: (c: T) => void; rotulo: string }) {
  const teclado = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key !== "ArrowRight" && ev.key !== "ArrowLeft") return;
    const i = abas.findIndex((a) => a.chave === ativa);
    aoMudar(abas[indiceVizinho(i, abas.length, ev.key === "ArrowRight" ? 1 : -1)].chave);
  };
  return (
    <div className="tl-abas" role="tablist" aria-label={rotulo} onKeyDown={teclado}>
      {abas.map((a) => <button key={a.chave} type="button" role="tab" aria-selected={a.chave === ativa} tabIndex={a.chave === ativa ? 0 : -1}
        onClick={() => aoMudar(a.chave)}>{a.texto}</button>)}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------------------------------------------------

export function GrupoCampos({ titulo, children }: { titulo: string; children: ReactNode }) {
  return <fieldset className="tl-grupo"><legend>{titulo}</legend>{children}</fieldset>;
}

export function Campo({ id, rotulo, erro, ajuda, unidade, largo, children }: {
  id: string; rotulo: string; erro?: string | null; ajuda?: ReactNode; unidade?: string; largo?: boolean; children: ReactNode;
}) {
  return (
    <div className={`tl-campo${largo ? " largo" : ""}${erro ? " erro" : ""}`}>
      <label htmlFor={id}>{rotulo}</label>
      {unidade ? <div className="tl-unid">{children}<span>{unidade}</span></div> : children}
      {erro ? <span className="tl-msg" id={`${id}-erro`}>{erro}</span> : ajuda ? <span className="tl-ajuda">{ajuda}</span> : null}
    </div>
  );
}

export function RodapeFormulario({ sujo, valido, salvando, aoCancelar, aoSalvar, rotuloSalvar = "Salvar" }: {
  sujo: boolean; valido: boolean; salvando?: boolean; aoCancelar?: () => void; aoSalvar: () => void; rotuloSalvar?: string;
}) {
  return (
    <div className="tl-rodape">
      <span className="tl-estado-form" aria-live="polite">{!sujo ? "Sem alterações" : valido ? "Alterações não salvas" : "Corrija os campos em vermelho"}</span>
      {aoCancelar && <button type="button" className="tl-fantasma" onClick={aoCancelar} disabled={salvando}>Cancelar</button>}
      <button type="button" className="nx-botao nx-botao-principal" onClick={aoSalvar} disabled={!sujo || !valido || salvando}>
        {salvando ? "Salvando…" : rotuloSalvar}</button>
    </div>
  );
}

/** Pergunta do navegador ao fechar ou recarregar a página com alterações não salvas. */
export function useAvisoAoSair(sujo: boolean): void {
  useEffect(() => {
    if (!sujo) return;
    const antes = (ev: BeforeUnloadEvent) => { ev.preventDefault(); ev.returnValue = ""; };
    window.addEventListener("beforeunload", antes);
    return () => window.removeEventListener("beforeunload", antes);
  }, [sujo]);
}

// ---------------------------------------------------------------------------------------------------------------------
// Diálogo de motivo (no lugar do window.prompt)
// ---------------------------------------------------------------------------------------------------------------------

export function DialogoMotivo({ aberto, titulo, texto, rotuloConfirmar, minimo = 5, exemplo, aoConfirmar, aoCancelar }: {
  aberto: boolean; titulo: string; texto?: ReactNode; rotuloConfirmar: string; minimo?: number; exemplo?: string;
  aoConfirmar: (motivo: string) => void | Promise<void>; aoCancelar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) { setMotivo(""); d.showModal(); }
    if (!aberto && d.open) d.close();
  }, [aberto]);
  return (
    <dialog ref={ref} className="tl-dialogo" onCancel={(e) => { e.preventDefault(); aoCancelar(); }}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (motivo.trim().length < minimo) return;
        setEnviando(true);
        try { await aoConfirmar(motivo.trim()); } finally { setEnviando(false); }
      }}>
        <h3>{titulo}</h3>
        {texto && <p className="nx-suave" style={{ margin: 0 }}>{texto}</p>}
        <div className="tl-campo"><label htmlFor={id}>Motivo (obrigatório)</label>
          <textarea id={id} rows={3} value={motivo} placeholder={exemplo} onChange={(e) => setMotivo(e.target.value)} autoFocus /></div>
        <div className="tl-linha-acoes" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="tl-fantasma" onClick={aoCancelar}>Cancelar</button>
          <button type="submit" className="nx-botao nx-botao-principal" disabled={motivo.trim().length < minimo || enviando}>{rotuloConfirmar}</button>
        </div>
      </form>
    </dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Estados: vazio, carregando, erro
// ---------------------------------------------------------------------------------------------------------------------

export function EstadoVazio({ titulo, texto, acao }: { titulo: string; texto?: ReactNode; acao?: ReactNode }) {
  return <div className="tl-vazio"><strong>{titulo}</strong>{texto && <span>{texto}</span>}{acao}</div>;
}

export function Esqueleto({ linhas = 6 }: { linhas?: number }) {
  return <div className="tl-esqueleto" aria-busy="true" aria-label="Carregando">{Array.from({ length: linhas }, (_, i) => <span key={i} />)}</div>;
}

export function ErroCarregar({ mensagem, aoTentar }: { mensagem: string; aoTentar?: () => void }) {
  return (
    <div className="tl-erro" role="alert"><p>{mensagem}</p>
      {aoTentar && <button type="button" className="nx-botao" onClick={aoTentar}>Tentar de novo</button>}</div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Aviso curto (toast): `avisar("PCR-03 salvo")` de qualquer tela; o Layout mostra
// ---------------------------------------------------------------------------------------------------------------------

export function avisar(texto: string, tipo: "ok" | "erro" = "ok"): void {
  window.dispatchEvent(new CustomEvent("nexora:aviso", { detail: { texto, tipo } }));
}

export function Avisos() {
  const [lista, setLista] = useState<{ id: number; texto: string; tipo: "ok" | "erro" }[]>([]);
  useEffect(() => {
    let n = 0;
    const ouvir = (ev: Event) => {
      const id = ++n, { texto, tipo } = (ev as CustomEvent<{ texto: string; tipo: "ok" | "erro" }>).detail;
      setLista((l) => [...l.slice(-2), { id, texto, tipo }]);
      window.setTimeout(() => setLista((l) => l.filter((a) => a.id !== id)), tipo === "erro" ? 6000 : 3000);
    };
    window.addEventListener("nexora:aviso", ouvir);
    return () => window.removeEventListener("nexora:aviso", ouvir);
  }, []);
  return (
    <div className="tl-toasts" aria-live="polite">
      {lista.map((a) => <div key={a.id} className={`tl-toast ${a.tipo}`} role={a.tipo === "erro" ? "alert" : "status"}>{a.texto}</div>)}
    </div>
  );
}
